import { useCallback, useEffect, useMemo, useState } from 'react';
import { Title, useNotify } from 'react-admin';
import RefreshIcon from '@mui/icons-material/Refresh';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';

import { getPatroniCluster, setPatroniLeader } from '../admin/adminPatroniApi';
import { extractPatroniMembers, type ClusterMember } from './patroniClusterParser';

export function PatroniClusterPage() {
    const notify = useNotify();
    const [payload, setPayload] = useState<unknown>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedMember, setSelectedMember] = useState<ClusterMember | null>(null);
    const [changingLeader, setChangingLeader] = useState(false);

    const loadCluster = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            setPayload(await getPatroniCluster());
        } catch (requestError) {
            setError(requestError instanceof Error ? requestError.message : 'Unable to load the Patroni cluster.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        void loadCluster();
    }, [loadCluster]);

    const members = useMemo(() => extractPatroniMembers(payload), [payload]);
    const leader = members.find((member) => member.isLeader);

    const confirmLeaderChange = async () => {
        if (!selectedMember) return;
        setChangingLeader(true);
        try {
            await setPatroniLeader(selectedMember.id);
            notify(`${selectedMember.label} leader request completed.`, { type: 'success' });
            setSelectedMember(null);
            await loadCluster();
        } catch (requestError) {
            notify(requestError instanceof Error ? requestError.message : 'Leader change failed.', { type: 'error' });
        } finally {
            setChangingLeader(false);
        }
    };

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            <Title title="Special Database" />
            <Stack spacing={2.5}>
                <Paper sx={{ p: { xs: 2.5, md: 3.5 }, borderRadius: 3 }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={2}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <Box sx={{ display: 'grid', placeItems: 'center', width: 48, height: 48, borderRadius: 2.5, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
                                <StorageOutlinedIcon />
                            </Box>
                            <Box>
                                <Typography variant="h4" fontWeight={800}>Special Database</Typography>
                                <Typography color="text.secondary">Patroni cluster leadership and member health</Typography>
                            </Box>
                        </Stack>
                        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => void loadCluster()} disabled={loading}>
                            Refresh
                        </Button>
                    </Stack>
                </Paper>

                {error ? <Alert severity="error" action={<Button onClick={() => void loadCluster()}>Retry</Button>}>{error}</Alert> : null}

                {!loading && leader ? (
                    <Alert severity="success" icon={<StorageOutlinedIcon />}>
                        Current leader: <strong>{leader.label}</strong> ({leader.id})
                    </Alert>
                ) : null}
                {!loading && members.length > 0 && !leader ? (
                    <Alert severity="warning">The cluster response did not identify a leader.</Alert>
                ) : null}

                <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
                    {loading ? (
                        <Box sx={{ minHeight: 240, display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>
                    ) : members.length === 0 ? (
                        <Box sx={{ p: 4 }}><Alert severity="warning">No cluster members were found in the Patroni response.</Alert></Box>
                    ) : (
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Member</TableCell>
                                    <TableCell>Role</TableCell>
                                    <TableCell>State</TableCell>
                                    <TableCell>Host / API</TableCell>
                                    <TableCell>Timeline</TableCell>
                                    <TableCell>Lag</TableCell>
                                    <TableCell align="right">Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {members.map((member) => (
                                    <TableRow key={member.id} hover sx={member.isLeader ? { bgcolor: 'success.50' } : undefined}>
                                        <TableCell>
                                            <Typography fontWeight={750}>{member.label}</Typography>
                                            <Typography variant="caption" color="text.secondary">{member.id}</Typography>
                                        </TableCell>
                                        <TableCell><Chip size="small" color={member.isLeader ? 'success' : 'default'} label={member.isLeader ? 'Leader' : member.role} /></TableCell>
                                        <TableCell>{member.state}</TableCell>
                                        <TableCell sx={{ maxWidth: 360, overflowWrap: 'anywhere' }}>{member.host}</TableCell>
                                        <TableCell>{member.timeline}</TableCell>
                                        <TableCell>{member.lag}</TableCell>
                                        <TableCell align="right">
                                            <Button
                                                variant={member.isLeader ? 'outlined' : 'contained'}
                                                disabled={member.isLeader || changingLeader}
                                                onClick={() => setSelectedMember(member)}
                                            >
                                                {member.isLeader ? 'Current leader' : 'Set leader'}
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </TableContainer>
            </Stack>

            <Dialog open={selectedMember !== null} onClose={changingLeader ? undefined : () => setSelectedMember(null)} maxWidth="sm" fullWidth>
                <DialogTitle>Change Patroni leader?</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mt: 1 }}>
                        This requests <strong>{selectedMember?.label}</strong> ({selectedMember?.id}) as the new database leader. A brief database interruption may occur.
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSelectedMember(null)} disabled={changingLeader}>Cancel</Button>
                    <Button variant="contained" color="warning" onClick={() => void confirmLeaderChange()} disabled={changingLeader}>
                        {changingLeader ? 'Changing…' : 'Confirm set leader'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}
